import { filter } from './filter';

/**
 * Converts an array of dot‑separated key paths into a nested key map.
 * Example: ["user.name", "address.city"] becomes { user: { name: true }, address: { city: true } }
 */
const buildNestedKeyMap = (keys) => {
  if (!keys) return null;
  return keys.reduce((acc, path) => {
    const parts = path.split('.');
    let current = acc;
    parts.forEach((part, index) => {
      if (!current[part]) {
        current[part] = index === parts.length - 1 ? true : {};
      }
      current = current[part];
    });
    return acc;
  }, {});
};

/**
 * Recursively checks if the data contains a match for the query,
 * based on the allowedKeys structure.
 */
const filterQuery = (data, query, allowedKeys) => {
  if (typeof data !== 'object' || data === null) {
    return filter(query, data);
  }
  if (Array.isArray(data)) {
    return data.some((item) => filterQuery(item, query, allowedKeys));
  }
  if (!allowedKeys) return false;
  return Object.keys(allowedKeys).some(
    (key) => key in data && filterQuery(data[key], query, allowedKeys[key]),
  );
};

/**
 * Main function to query an object or array.
 * - For arrays, full objects are returned if any allowed key matches.
 * - For objects:
 *   - Flat mode: if no keys provided or provided keys are flat, return only matching keys.
 *   - Nested mode: if keysOfInterest include dot‑notation, return full branches for matching keys.
 */
export const queryObject = (obj, query, keysOfInterest = null) => {
  if (typeof obj !== 'object' || obj === null) {
    return Array.isArray(obj) ? [] : {};
  }

  // Determine if we are in nested mode (if any provided key contains a dot)
  const isNestedMode = keysOfInterest ? keysOfInterest.some((k) => k.includes('.')) : false;

  // For arrays, always return full objects that match.
  if (Array.isArray(obj)) {
    const keyMap = keysOfInterest
      ? buildNestedKeyMap(keysOfInterest)
      : buildNestedKeyMap(Object.keys(obj[0] || {}));
    return obj.filter((item) => filterQuery(item, query, keyMap));
  }

  // Flat mode: when no keys are provided or keys are flat (no dot‑notation)
  if (!keysOfInterest || (keysOfInterest && !isNestedMode)) {
    const allowedKeys = keysOfInterest || Object.keys(obj);
    const result = {};
    allowedKeys.forEach((key) => {
      if (key in obj && filter(query, obj[key])) {
        result[key] = obj[key];
      }
    });
    return result;
  }

  // Nested mode: only include branches specified in keysOfInterest.
  const keyMap = buildNestedKeyMap(keysOfInterest);
  const result = {};
  Object.keys(keyMap).forEach((key) => {
    if (key in obj && filterQuery(obj[key], query, keyMap[key])) {
      result[key] = obj[key];
    }
  });
  return result;
};

/**
 * Filters an array of objects based on a filterList.
 * For each object, it checks that every key in filterList contains the filter text.
 */
export const queryColumns = (obj, filterList) => {
  return obj.filter((item) => {
    for (const key in filterList) {
      // For numeric values, convert to string.
      if (Number.isInteger(item[key])) {
        if (
          item[key] === undefined ||
          ('' + item[key]).toLowerCase().indexOf(('' + filterList[key]).toLowerCase()) === -1
        ) {
          return false;
        }
      } else if (
        item[key] === undefined ||
        item[key].toLowerCase().indexOf(filterList[key].toLowerCase()) === -1
      ) {
        return false;
      }
    }
    return true;
  });
};
